"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronRight, Bookmark } from "lucide-react";
import { SUBSPECIALTY_MAP, type ViewMode } from "@/lib/constants";
import type { DiseaseFrontmatter } from "@/lib/content";
import { Badge } from "@/components/ui/badge";
import { ViewModeToggle } from "@/components/layout/ViewModeToggle";
import { DiseaseTOC } from "@/components/layout/DiseaseTOC";
import { SummaryCard } from "@/components/disease/SummaryCard";
import { ThreeLineSummary } from "@/components/disease/ThreeLineSummary";
import { QuickGuide } from "@/components/disease/QuickGuide";
import { PatientScript } from "@/components/disease/PatientScript";
import { QuickClinicalView } from "@/components/disease/QuickClinicalView";
import { useBookmarks } from "@/lib/bookmarks";

function slugToName(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

interface DiseasePageClientProps {
  frontmatter: DiseaseFrontmatter;
  subspecialty: string;
  slug: string;
  children: React.ReactNode;
  papersPanel?: React.ReactNode;
  slugToSubspec?: Record<string, string>;
}

export function DiseasePageClient({
  frontmatter: fm,
  subspecialty,
  slug,
  children,
  papersPanel,
  slugToSubspec = {},
}: DiseasePageClientProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("standard");
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const sub = SUBSPECIALTY_MAP[subspecialty];
  const isQuick = viewMode === "quick";
  const bookmarked = isBookmarked(slug);

  return (
    <div className="flex gap-8">
      {/* Main content */}
      <div className="min-w-0 flex-1">
        {/* Breadcrumb */}
        <nav className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link href="/" className="hover:text-foreground">
            홈
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link href={`/${subspecialty}`} className="hover:text-foreground">
            {sub?.name || subspecialty}
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground">{fm.title}</span>
        </nav>

        {/* View mode toggle + Bookmark */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => toggleBookmark(slug, subspecialty, fm.title)}
              className="mr-1 rounded-md p-1.5 hover:bg-accent transition-colors"
              title={bookmarked ? "북마크 해제" : "북마크 추가"}
            >
              <Bookmark
                className={`h-4.5 w-4.5 ${
                  bookmarked
                    ? "fill-primary text-primary"
                    : "text-muted-foreground"
                }`}
              />
            </button>
            {fm.emergency && (
              <Badge variant="destructive" className="text-xs">
                응급
              </Badge>
            )}
            {!isQuick && fm.examImportance === "high-yield" && (
              <Badge className="bg-clinical-amber/15 text-clinical-amber text-xs">
                High-Yield
              </Badge>
            )}
            {!isQuick && fm.difficulty && (
              <Badge variant="secondary" className="text-xs">
                {fm.difficulty}
              </Badge>
            )}
            {isQuick && (
              <Badge className="bg-clinical-blue/15 text-clinical-blue text-xs">
                Quick Clinical
              </Badge>
            )}
          </div>
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        </div>

        {/* Summary Card (양쪽 모드) */}
        {fm.summaryCard && (
          <div className="mb-5">
            <SummaryCard
              title={fm.title}
              titleEn={fm.titleEn}
              essence={fm.summaryCard.essence}
              emergencyLevel={fm.summaryCard.emergencyLevel}
              keyTest={fm.summaryCard.keyTest}
              firstLineTx={fm.summaryCard.firstLineTx}
              emergency={fm.emergency}
            />
          </div>
        )}

        {/* Quick 모드: Quick Guide 최상단 */}
        {isQuick && fm.quickGuide && (
          <div id="quick-guide" data-toc-id="quick-guide" className="mb-5 scroll-mt-20">
            <QuickGuide
              isEmergency={fm.quickGuide.isEmergency}
              todayTests={fm.quickGuide.todayTests}
              immediateTx={fm.quickGuide.immediateTx}
              reportWhen={fm.quickGuide.reportWhen}
            />
          </div>
        )}

        {/* 3-line summary (양쪽 모드) */}
        {fm.threeLineSummary && (
          <div id={isQuick ? "three-line" : undefined} data-toc-id={isQuick ? "three-line" : undefined} className="mb-5 scroll-mt-20">
            <ThreeLineSummary
              core={fm.threeLineSummary.core}
              redFlag={fm.threeLineSummary.redFlag}
              treatment={fm.threeLineSummary.treatment}
            />
          </div>
        )}

        {/* Red Flags (양쪽 모드) */}
        {fm.redFlags && fm.redFlags.length > 0 && (
          <div id={isQuick ? "red-flags" : undefined} data-toc-id={isQuick ? "red-flags" : undefined} className="mb-5 scroll-mt-20 rounded-xl border border-clinical-red/30 bg-clinical-red/5 p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-clinical-red">
              Red Flags
            </p>
            <ul className="space-y-1">
              {fm.redFlags.map((flag, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-clinical-red" />
                  {flag}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ===== Quick Clinical 모드 전용 콘텐츠 ===== */}
        {isQuick && fm.quickClinical && (
          <QuickClinicalView data={fm.quickClinical} />
        )}

        {/* ===== Standard 모드 전용 콘텐츠 ===== */}
        {!isQuick && (
          <>
            {/* Quick Guide (Standard에서는 Red Flags 아래) */}
            {fm.quickGuide && (
              <div className="mb-5">
                <QuickGuide
                  isEmergency={fm.quickGuide.isEmergency}
                  todayTests={fm.quickGuide.todayTests}
                  immediateTx={fm.quickGuide.immediateTx}
                  reportWhen={fm.quickGuide.reportWhen}
                />
              </div>
            )}

            {/* Patient Script */}
            {fm.patientScript && (
              <div className="mb-5">
                <PatientScript script={fm.patientScript} />
              </div>
            )}

            {/* MDX Content (교과서 본문) */}
            <article className="prose">{children}</article>
          </>
        )}

        {/* Related info (양쪽 모드) */}
        {(fm.differentials || fm.relatedDiseases || fm.specialTests) && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fm.specialTests && fm.specialTests.length > 0 && (
              <div className="rounded-xl border border-border p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  주요 검사
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {fm.specialTests.map((test) => (
                    <Badge key={test} variant="secondary">
                      {test}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {fm.differentials && fm.differentials.length > 0 && (
              <div className="rounded-xl border border-border p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  감별진단
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {fm.differentials.map((d) => {
                    const targetSub = slugToSubspec[d];
                    if (!targetSub) {
                      return (
                        <Badge
                          key={d}
                          variant="outline"
                          className="opacity-50 cursor-not-allowed"
                          title="아직 등록되지 않은 질환입니다"
                        >
                          {slugToName(d)}
                        </Badge>
                      );
                    }
                    return (
                      <Link key={d} href={`/${targetSub}/${d}`}>
                        <Badge
                          variant="outline"
                          className="cursor-pointer transition-colors hover:bg-accent"
                        >
                          {slugToName(d)}
                        </Badge>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {fm.relatedDiseases && fm.relatedDiseases.length > 0 && (
              <div className="rounded-xl border border-border p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  연관 질환
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {fm.relatedDiseases.map((d) => {
                    const targetSub = slugToSubspec[d];
                    if (!targetSub) {
                      return (
                        <Badge
                          key={d}
                          variant="outline"
                          className="opacity-50 cursor-not-allowed"
                          title="아직 등록되지 않은 질환입니다"
                        >
                          {slugToName(d)}
                        </Badge>
                      );
                    }
                    return (
                      <Link key={d} href={`/${targetSub}/${d}`}>
                        <Badge
                          variant="outline"
                          className="cursor-pointer transition-colors hover:bg-accent"
                        >
                          {slugToName(d)}
                        </Badge>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guideline summary (양쪽 모드) */}
        {fm.guidelineSummary && (
          <div className="mt-4 rounded-xl border border-clinical-purple/20 bg-clinical-purple/5 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-clinical-purple">
              Guideline Snapshot
            </p>
            <p className="text-sm">{fm.guidelineSummary}</p>
          </div>
        )}
      </div>

      {/* Right sidebar - TOC + Papers */}
      <aside className="hidden w-64 shrink-0 xl:block">
        <div className="sticky top-20 space-y-6 max-h-[calc(100vh-6rem)] overflow-y-auto">
          <DiseaseTOC viewMode={viewMode} />
          {papersPanel && (
            <div className="rounded-xl border border-border p-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                관련 논문
              </h3>
              {papersPanel}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
